'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Title from '@/components/content/blocks/shared/TitleBlock'
import ChatTileMask from '@/components/content/home/hero/TileMask'
import Container from '@/components/structure/Container'
import Marquee from '@/components/ui/Marquee'
import Subscribe from '@/components/ui/Subscribe'
import { cn, convertLexicalToString } from '@/lib/utils'
import { setPageTheme } from '@/lib/pageTheme'
import type { Page } from '@/payload-types'
import type { Block } from '@/types/app'

type HeroTemplate = Extract<NonNullable<Page['layout']>[number], { blockType: 'heroTemplate' }>
type ImageMarqueeProps = { images: HeroTemplate['images']; className?: string }

export default function HeroBlock({
  title,
  images,
  theme,
  footer,
  subscribe,
  subscribeButton,
  subscribeBody,
  paddingTop,
  paddingBottom,
}: HeroTemplate) {
  const [rows, setRows] = useState<{ firstRow: typeof images; secondRow: typeof images }>({
    firstRow: images?.slice(0, Math.ceil((images?.length || 0) / 2)) || [],
    secondRow: images?.slice(Math.ceil((images?.length || 0) / 2)) || [],
  })

  useEffect(() => {
    if (!images || images.length === 0) return
    if (images.length < 10) {
      const shuffled1 = [...images].sort(() => Math.random() - 0.5)
      const shuffled2 = [...images].sort(() => Math.random() - 0.5)
      setRows({ firstRow: shuffled1, secondRow: shuffled2 })
    }
  }, [images])

  const { firstRow, secondRow } = rows

  useEffect(() => {
    setPageTheme(theme === 'dark' ? 'dark' : 'light')
    return () => setPageTheme(null)
  }, [theme])

  function normalizePadding(value: unknown) {
    if (value === 'none' || value === 'small' || value === 'medium' || value === 'large') {
      return value
    }
    if (value === 0) {
      return 'none'
    }
    return 'medium'
  }

  const normalizedPaddingTop = normalizePadding(paddingTop)
  const normalizedPaddingBottom = normalizePadding(paddingBottom)

  // 'none' still reserves nav clearance — the public header is h-20 (80px)
  // fixed at top, so pt-0 would render the title under the nav on a Page
  // whose first block is this hero (e.g. /careers). pt-20 md:pt-24 gives the
  // minimum clearance with no extra spacing. "no top padding" in the admin
  // UI means "no extra space beyond what's needed for the layout to work."
  const paddingTopClass =
    normalizedPaddingTop === 'none'
      ? 'pt-20 md:pt-24'
      : normalizedPaddingTop === 'small'
        ? 'pt-24 md:pt-28'
        : normalizedPaddingTop === 'medium'
          ? 'pt-32 md:pt-36'
          : 'pt-44 md:pt-56'
  const paddingBottomClass =
    normalizedPaddingBottom === 'none'
      ? 'pb-0'
      : normalizedPaddingBottom === 'small'
        ? 'pb-4 md:pb-8'
        : normalizedPaddingBottom === 'medium'
          ? 'pb-12 md:pb-16'
          : 'pb-24 md:pb-36'

  const hasImages = images && images.length > 0

  return (
    <div
      className={cn(
        'flex flex-col',
        paddingBottomClass !== 'pb-0' ? paddingBottomClass : 'pb-12 md:pb-16',
      )}
    >
      <div
        className={cn(
          'relative flex h-fit flex-col justify-center',
          paddingTopClass,
          paddingBottomClass,
        )}
      >
        <Container>
          <Title
            title={title}
            props={{
              first: true,
              className: 'relative z-10 px-10',
              maxWidth: 760,
              theme: theme ?? undefined,
            }}
          />
          {subscribe && subscribeBody?.root?.children && (
            <Subscribe
              copy={subscribeBody?.root?.children as Block[]}
              button={subscribeButton}
              className="relative z-20"
            />
          )}
        </Container>
        <ChatTileMask invert={theme === 'dark'} />
      </div>

      {hasImages && (
        <div className="relative flex h-fit flex-col gap-4 bg-gradient-to-b from-gray-950 via-primary-950 via-[40%] to-white to-[90%]">
          <ImageMarquee images={firstRow} />
          <ImageMarquee images={secondRow} className="mb-16 -translate-x-[168px]" />

          {footer &&
            footer?.footerText?.root.children &&
            footer?.footerText?.root.children.length > 0 && (
              <div className="mx-auto flex gap-4">
                {footer?.footerImage &&
                  typeof footer.footerImage !== 'number' &&
                  footer.footerImage.url && (
                    <Image
                      src={footer.footerImage.url}
                      alt={footer.footerImage.alt || ''}
                      width={22}
                      height={22}
                      className="h-[22px] w-[22px] shrink-0 rounded-full border border-gray-200 bg-white"
                    />
                  )}
                <p
                  className="text-14 text-gray-500 [&>span]:ml-1.5 [&>span]:font-medium [&>span]:text-gray-950"
                  dangerouslySetInnerHTML={{
                    __html: convertLexicalToString(footer?.footerText?.root.children as Block[]),
                  }}
                />
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export function ImageMarquee({ images, className }: ImageMarqueeProps) {
  if (!images) return null

  return (
    <Marquee mask={false}>
      <div className={cn('flex gap-4 overflow-hidden pl-4', className)}>
        {images.map((block, index) => {
          const image = block.image && typeof block.image !== 'number' ? block.image : null
          return (
            <div
              key={`item-${index}`}
              className="relative h-48 w-80 shrink-0 overflow-hidden rounded-2xl bg-gray-100"
            >
              {image?.url && (
                <Image
                  src={image.url}
                  alt={image.alt || ''}
                  fill
                  sizes="320px"
                  priority={index < 5}
                  className="object-cover"
                />
              )}
            </div>
          )
        })}
      </div>
    </Marquee>
  )
}
